import '../styles/main.scss';
import ErrorPage from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { ErrorBoundary } from '@buzzfeed/react-components';
import { createPixiedustBroadcaster } from '@buzzfeed/pixiedust';
import { getDestination } from '@buzzfeed/bf-utils/lib/domain';
import { isBFIslandWebview } from '@buzzfeed/bf-utils/lib/webview';
import { captureException } from '../sentry.client.config.js';
import { useDocumentReferrerPolyfill } from '../hooks/tracking/useDocumentReferrerPolyfill';
import { TrackingContext } from '../contexts';
import { ShareTrayContext } from '../contexts/ShareTrayContext';
import { setNativeScrollDisabledByKey } from '../utils/nativeScroll';
import ABeagleProvider from '../components/ABeagleProvider';
import styles from './index.module.scss';
import { CLUSTER, SERVICE_NAME } from '../constants';
import Navbar from '../components/Navbar/index.jsx';
import Footer from '../components/Footer';
import loadingGif from '../public/static/images/seagullLoadingGif.gif';

const [sendEvent] = createPixiedustBroadcaster({
  env: CLUSTER,
});

function Application({ Component, pageProps }) {
  useDocumentReferrerPolyfill();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isIslandWebview, setIsIslandWebview] = useState(false);
  const [isShareTrayOpen, setIsShareTrayOpen] = useState(false);

  // Disable native (For You feed) scrolling while the share tray is open
  // anywhere in the app, and re-enable it when it closes.
  useEffect(() => {
    setNativeScrollDisabledByKey('shareTray', isShareTrayOpen);
  }, [isShareTrayOpen]);

  // Global loading state for all route changes
  useEffect(() => {
    const handleRouteChangeStart = () => setIsLoading(true);
    const handleRouteChangeComplete = () => setIsLoading(false);
    const handleRouteChangeError = () => setIsLoading(false);

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router.events]);

  // check if in webview
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const simulateIsland = urlParams.get('simulate_island');
    const islandWebview = isBFIslandWebview() || simulateIsland === 'app';
    setIsIslandWebview(islandWebview);
    document.documentElement.style.overflowY = islandWebview ? '' : 'scroll';

    return () => {
      document.documentElement.style.overflowY = '';
    };
  }, []);

  // Check if we're on the base route (homepage)
  const isHomepage = router.pathname === '/';

  const showNavBar =
    router.pathname !== '/settings' &&
    !router.pathname.startsWith('/delete-account') &&
    router.pathname !== '/settings/avatar' &&
    !isIslandWebview;

  const onError = (error, errorInfo) => {
    const destination = getDestination(window.location.href);

    // send pixiedust event that will get turned into a datadog metric
    const event = {
      type: 'instrumentation',
      destination,
      service: SERVICE_NAME,
      platform: 'web',
      target: 'error',
      value: 'unhandled_page',
      source: 'web',
    };

    sendEvent(event);

    captureException(error, {
      level: 'fatal',
      extra: errorInfo,
      tags: {
        error_boundary: 'application',
        destination,
      },
    });
  };

  return (
    <ErrorBoundary
      fallbackRender={() => (
        <ErrorPage statusCode={500} title="An unexpected error has occurred" />
      )}
      onError={(error, errorInfo) => {
        console.error(`Unexpected page error: ${error.message}`);
        onError(error, errorInfo);
      }}
    >
      <Head>
        <title>
          {isHomepage
            ? 'BF Island - AI Image Generator Built for Group Chat'
            : 'BuzzFeed Island'}
        </title>
        <meta
          name="title"
          content={
            isHomepage
              ? 'BF Island - AI Image Generator Built for Group Chat'
              : 'BuzzFeed Island'
          }
        />
        <meta
          name="description"
          content={
            isHomepage
              ? 'Create AI images, share them instantly, and chat with friends. BF Island is an AI image generator built for group chats, memes, and conversation starters.'
              : undefined
          }
        />
        <meta
          name="copyright"
          content="Copyright BuzzFeed, Inc. All rights reserved."
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <link rel="icon" type="image/png" href="/static/images/favicon.png" />
      </Head>
      <TrackingContext.Provider
        value={{
          ...pageProps.tracking,
        }}
      >
        <ShareTrayContext.Provider value={{ isShareTrayOpen, setIsShareTrayOpen }}>
          <ABeagleProvider>
            {showNavBar && <Navbar isHomepage={isHomepage} />}
            {isLoading && !isIslandWebview ? (
              <div className={styles.loadingContainer}>
                <img src={loadingGif} alt="Loading..." />
              </div>
            ) : (
              <>
                <div className={`${styles.container} ${isShareTrayOpen ? styles.shareTrayOpen : ''} ${showNavBar ? styles.showNav : ''}`}>
                  <Component {...pageProps} />
                </div>
                {!isIslandWebview && <Footer />}
              </>
            )}
          </ABeagleProvider>
        </ShareTrayContext.Provider>
      </TrackingContext.Provider>
    </ErrorBoundary>
  );
}

export default Application;
